import { useTranslation } from 'react-i18next';
import SiteLayout from '../layout/SiteLayout';
import Seo from '../seo/Seo';
import AppCta from './AppCta';
import {
  Breadcrumbs,
  DirectAnswer,
  DisclaimerNote,
  FaqSection,
  RelatedLinks,
  ReviewedBy,
  SourcesList,
} from './ContentBlocks';
import { articleJsonLd, breadcrumbJsonLd, faqJsonLd, howToServeJsonLd, webPageJsonLd } from '../../lib/jsonld';
import { getFoodBySlug, getGuideBySlug, getRecipeBySlug, localizeItem } from '../../content';
import { useContentLang, useLocalizedItem, useLocalizedPath } from '../../hooks/useLocalizedContent';
import './Knowledge.css';

function relatedItems(slugs, getter, nameKey, lang, toPath) {
  return (slugs || [])
    .map(getter)
    .filter(Boolean)
    .map((item) => {
      const loc = localizeItem(item, lang);
      return { name: loc[nameKey] || loc.title || loc.name, path: toPath(loc.path) };
    });
}

function FoodPage({ food: foodProp }) {
  const { t } = useTranslation();
  const lang = useContentLang();
  const food = useLocalizedItem(foodProp);
  const toPath = useLocalizedPath();
  const pagePath = toPath(food.path);
  const displayName = food.name.toLowerCase();
  const plural = food.plural || `${displayName}s`;

  const breadcrumbs = [
    { name: t('knowledge.page.home'), path: '/' },
    { name: t('knowledge.page.foods'), path: toPath('/foods') },
    { name: food.name, path: pagePath },
  ];

  const relatedFoods = relatedItems(food.relatedFoods, getFoodBySlug, 'name', lang, toPath);
  const relatedGuides = relatedItems(food.relatedGuides, getGuideBySlug, 'title', lang, toPath);
  const relatedRecipes = relatedItems(food.relatedRecipes, getRecipeBySlug, 'title', lang, toPath);

  return (
    <SiteLayout>
      <Seo
        title={food.h1}
        description={food.metaDescription || food.shortAnswer}
        path={pagePath}
        image={food.imageUrl}
        lang={lang}
        jsonLd={[
          webPageJsonLd({
            title: food.h1,
            description: food.shortAnswer,
            path: pagePath,
            inLanguage: lang,
          }),
          breadcrumbJsonLd(breadcrumbs),
          articleJsonLd({
            title: food.h1,
            description: food.shortAnswer,
            path: pagePath,
            image: food.imageUrl,
            datePublished: food.createdAt,
            dateModified: food.reviewedAt,
            authorName: food.reviewedBy?.name,
            inLanguage: lang,
          }),
          howToServeJsonLd({
            name: food.name,
            path: pagePath,
            servings: food.servings,
            inLanguage: lang,
          }),
          faqJsonLd(food.faqs),
        ]}
      />
      <article className="kb-page">
        <Breadcrumbs items={breadcrumbs} />
        <header className="kb-hero">
          {food.imageUrl ? (
            <img className="kb-hero-image" src={food.imageUrl} alt={t('knowledge.page.foodHeroAlt', { name: food.name })} />
          ) : null}
          <div>
            <p className="kb-kicker">{t('knowledge.page.foodKicker')}</p>
            <h1>{food.h1}</h1>
            <p className="kb-meta">
              {t('knowledge.page.fromMonths', { count: food.introductionAge })}
              {food.isCommonAllergen ? ` · ${t('knowledge.page.commonAllergen')}` : ''}
            </p>
          </div>
        </header>

        <DirectAnswer>{food.shortAnswer}</DirectAnswer>
        <DisclaimerNote />

        <section className="kb-section">
          <h2>{food.canBabiesEat.question}</h2>
          <p className="kb-lead">{food.canBabiesEat.answer}</p>
        </section>

        <section className="kb-section">
          <h2>{food.whenToIntroduce.question}</h2>
          <p className="kb-lead">{food.whenToIntroduce.answer}</p>
          {food.nutritionSummary ? <p>{food.nutritionSummary}</p> : null}
        </section>

        <section className="kb-section" id="how-to-serve">
          <h2>{t('knowledge.page.howToServe', { name: displayName })}</h2>
          <div className="serving-grid">
            {food.servings.map((serving) => (
              <figure key={serving.ageLabel} className="serving-card">
                {serving.imageUrl ? (
                  <img
                    src={serving.imageUrl}
                    alt={t('knowledge.page.serveAlt', { name: food.name, age: serving.ageLabel })}
                  />
                ) : null}
                <figcaption>
                  <h3>{t('knowledge.page.serveAtAge', { name: displayName, age: serving.ageLabel })}</h3>
                  <p>{serving.description}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="kb-section">
          <h2>{t('knowledge.page.choking', { plural })}</h2>
          <p className="kb-lead">{food.choking}</p>
        </section>

        <section className="kb-section">
          <h2>{t('knowledge.page.allergen', { plural })}</h2>
          <p className="kb-lead">{food.allergenInfo}</p>
        </section>

        <section className="kb-section">
          <h2>{t('knowledge.page.portion', { name: displayName })}</h2>
          <p className="kb-lead">{food.portionGuidance}</p>
        </section>

        {food.needsCooking ? (
          <section className="kb-section">
            <h2>{t('knowledge.page.cooking', { plural })}</h2>
            <p className="kb-lead">{food.needsCooking}</p>
          </section>
        ) : null}

        {food.frozen ? (
          <section className="kb-section">
            <h2>{t('knowledge.page.frozen', { plural })}</h2>
            <p className="kb-lead">{food.frozen}</p>
          </section>
        ) : null}

        {food.preparation?.length ? (
          <section className="kb-section">
            <h2>{t('knowledge.page.prepare', { name: displayName })}</h2>
            <ul>
              {food.preparation.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          </section>
        ) : null}

        {food.storage?.length ? (
          <section className="kb-section">
            <h2>{t('knowledge.page.storage')}</h2>
            <ul>
              {food.storage.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          </section>
        ) : null}

        <RelatedLinks title={t('knowledge.page.easyRecipes', { name: displayName })} items={relatedRecipes} />
        <FaqSection faqs={food.faqs} />
        <SourcesList sources={food.sources} />
        <ReviewedBy reviewer={food.reviewedBy} reviewedAt={food.reviewedAt} lang={lang} />
        <RelatedLinks title={t('knowledge.page.relatedFoods')} items={relatedFoods} />
        <RelatedLinks title={t('knowledge.page.relatedGuides')} items={relatedGuides} />
        <AppCta
          title={t('knowledge.page.foodCtaTitle', { name: displayName })}
          text={t('knowledge.page.foodCtaText')}
        />
      </article>
    </SiteLayout>
  );
}

export default FoodPage;
export { relatedItems };
